// @ts-nocheck
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, MapPin, Utensils, PlusCircle, Loader2, Clipboard, X, Plus } from 'lucide-react';
import { SearchFilters, VideoPlatform } from '../types';

interface SearchBarProps {
  // Controlled search term and filters from parent
  searchTerm: string;
  filters: SearchFilters;
  onSearchTermChange: (term: string) => void;
  onFiltersChange: (filters: SearchFilters) => void;
  // Triggers an immediate search (e.g., Enter key or button click)
  onSearch: (searchTerm: string, filters: SearchFilters) => void;
  // Add video flow
  onAddVideo: (videoUrl: string) => Promise<void>;
  // When the app is adding (global overlay), reflect busy state on buttons
  addingActive?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({ searchTerm, filters, onSearchTermChange, onFiltersChange, onSearch, onAddVideo, addingActive = false }) => {
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [isAddingVideo, setIsAddingVideo] = useState<boolean>(false);
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const busy = isAddingVideo || addingActive;

  


  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) onSearchTermChange(text.trim());
      inputRef.current?.focus();
    } catch {}
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // If the input looks like a URL, treat Enter as Add Video for convenience
    if (/^https?:\/\//i.test(searchTerm.trim())) {
      handleAddInline();
    } else {
      onSearch(searchTerm, filters);
    }
  };

  const handleFilterChange = (key: keyof SearchFilters, value: string) => {
    const newFilters = { ...filters, [key]: value } as SearchFilters;
    onFiltersChange(newFilters);
  };

  const handleAddInline = async () => {

    const url = searchTerm.trim();
    if (!/^https?:\/\//i.test(url)) return;
    setIsAddingVideo(true);
    try {
  // Clear immediately so the user sees the full grid and the URL doesn’t linger
  onSearchTermChange('');
  inputRef.current?.blur();
  await onAddVideo(url);
  // no-op: already cleared above
    } catch (err) {
      console.error('Error adding video:', err);
    } finally {
      setIsAddingVideo(false);
    }
  };

  const platforms: { value: VideoPlatform | 'all'; label: string }[] = [
    { value: 'all', label: 'Todas las plataformas' },
    { value: 'TikTok', label: 'TikTok' },
    { value: 'Instagram', label: 'Instagram' },
    { value: 'YouTube', label: 'YouTube' },
    { value: 'Facebook', label: 'Facebook' }
  ];

  const cuisines: { value: string; label: string }[] = [
    { value: 'all', label: 'Todas las cocinas' },
    { value: 'italiana', label: 'Italiana' },
    { value: 'japonesa', label: 'Japonesa' },
    { value: 'mexicana', label: 'Mexicana' },
    { value: 'francesa', label: 'Francesa' },
    { value: 'china', label: 'China' },
    { value: 'india', label: 'India' }
  ];

  return (
    <div 
      className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-3xl p-6 shadow-xl mb-8"
    >
      <form onSubmit={handleSearch} className="space-y-4">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              ref={inputRef}
              value={searchTerm}
              onChange={(e) => onSearchTermChange(e.target.value)}
              placeholder="Buscar restaurantes, platos, ubicaciones..."
              className="w-full pl-12 pr-12 py-4 bg-gray-50/80 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
            />
            <button
              type="button"
              onClick={pasteFromClipboard}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
              title="Pegar del portapapeles"
            >
              <Clipboard className="w-5 h-5" />
            </button>
          </div>
          
          <motion.button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-6 py-4 rounded-2xl font-semibold flex items-center gap-2 ${
              showFilters 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            whileHover={false}
            whileTap={false}
          >
            <Filter className="w-5 h-5" />
            Filtros
          </motion.button>

          <motion.button
            type="button"
            onClick={handleAddInline}
            className={`px-6 py-4 rounded-2xl font-semibold transition-all duration-300 flex items-center gap-2 ${
              /^https?:\/\//i.test(searchTerm) ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-500'
            }`}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            disabled={!/^https?:\/\//i.test(searchTerm) || busy}
            aria-busy={busy}

          >
            {busy ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Añadiendo…
              </>
            ) : (
              <>
                <PlusCircle className="w-5 h-5" />
                Añadir
              </>
            )}
          </motion.button>

          <motion.button
            type="submit"
            className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-2xl font-semibold hover:shadow-lg transition-all duration-300"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            Buscar
          </motion.button>
          

        </div>

  <div className="overflow-hidden" style={{ height: showFilters ? 'auto' as any : 0, opacity: showFilters ? 1 : 0, transition: 'opacity 150ms ease-out' }}>
          <div className="pt-4 border-t border-gray-200/50 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plataforma
              </label>
              <select
                value={filters.platform}
                onChange={(e) => handleFilterChange('platform', e.target.value as VideoPlatform | 'all')}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              >
                {platforms.map(platform => (
                  <option key={platform.value} value={platform.value}>
                    {platform.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Utensils className="w-4 h-4 inline mr-1" />
                Tipo de Cocina
              </label>
              <select
                value={filters.cuisine}
                onChange={(e) => handleFilterChange('cuisine', e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              >
                {cuisines.map(cuisine => (
                  <option key={cuisine.value} value={cuisine.value}>
                    {cuisine.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Ubicación
              </label>
              <input
                type="text"
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                placeholder="Ciudad, país..."
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
            </div>
          </div>
  </div>

  {/* Removed expandable Add panel; Add is inline in the top row */}

      {/* Mobile FAB + bottom sheet to unify single search/add entry */}
      <button
        className="md:hidden fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-blue-600 text-white shadow-lg flex items-center justify-center"
        onClick={() => setMobileOpen(true)}
        aria-label="Abrir búsqueda"
      >
        <Plus className="w-6 h-6" />
      </button>
  {mobileOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={()=>setMobileOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-white shadow-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">Buscar o pegar enlace</h3>
              <button onClick={()=>setMobileOpen(false)} aria-label="Cerrar" className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
  <form onSubmit={(e)=>{ e.preventDefault(); if (/^https?:\/\//i.test(searchTerm.trim())) { handleAddInline(); setMobileOpen(false); } else { onSearch(searchTerm, filters); setMobileOpen(false); } }} className="space-y-3">
              <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2 border border-gray-200">
                <Search className="w-4 h-4 text-gray-500" />
                <input
      value={searchTerm}
      onChange={(e) => onSearchTermChange(e.target.value)}
                  placeholder="Pega un enlace o escribe para buscar"
                  className="w-full bg-transparent outline-none text-sm"
                />
                <button type="button" onClick={pasteFromClipboard} className="text-gray-600 hover:text-gray-800" title="Pegar del portapapeles">
                  <Clipboard className="w-4 h-4" />
                </button>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="flex-1 bg-gray-900 text-white py-2 rounded-xl text-sm">Buscar</button>
                <button
                  type="button"
                  onClick={()=>{ handleAddInline(); setMobileOpen(false); }}
                  disabled={!/^https?:\/\//i.test(searchTerm) || busy}
                  aria-busy={busy}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-xl text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Añadiendo…</>) : 'Añadir enlace'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </form>
    </div>
  );
};

export default SearchBar;